import { useState, useCallback, useContext } from 'react';
import { LoggerContext } from "context/LoggerContext";

/**
 * Hook for managing tickets in checkout
 * @param {Object} formData - Form data containing customer info
 * @returns {Object} Ticket management methods and state
 */
const useTicketManager = (formData) => {
	const { logDebug } = useContext(LoggerContext);
	const [expandedTickets, setExpandedTickets] = useState([]);

	/**
	 * Generates ticket objects for event articles based on article data and existing invitations
	 * @param {Array} articles - Array of article objects
	 * @param {Array} existingInvitations - Array of existing invitation objects
	 * @returns {Array} - Array of ticket objects
	 */
	const generateTicketsForEventArticles = useCallback((articles, existingInvitations = []) => {
		if (!articles || !Array.isArray(articles)) return [];

		// Filter event articles
		const eventArticles = articles.filter(article => article?.instance?.kind_id === 3);
		if (eventArticles.length === 0) return [];

		// Debug log for articles
		logDebug("Generating tickets for event articles:", {
			articleCount: eventArticles.length,
			articles: eventArticles
		});

		// Create new tickets array to return
		const newTickets = [];

		// Process each event article
		eventArticles.forEach(article => {
			// Get the count of tickets needed for this article
			const ticketCount = parseInt(article.count || 1, 10);

			// Direct extraction of agendas and requiredAmount from this source
			const selectable_agendas = article?.instance?.event?.selectable_agendas || [];
			const requiredAgendaAmount = article?.instance?.event?.linked_agenda_Amount || 0;

			// Debug logging for each ticket
			logDebug(`Article ${article.id} event data:`, {
				articleId: article.id,
				availableAgendas: selectable_agendas,
				agendaCount: selectable_agendas?.length || 0,
				requiredAmount: requiredAgendaAmount,
				ticketCount
			});

			// For each count, create a ticket object
			for (let i = 0; i < ticketCount; i++) {
				// Check if we have an existing invitation for this ticket
				const existingInvitation = existingInvitations.find(inv =>
					inv.article_id === article.id &&
					inv.extra_info?.ticket_number === (i + 1)
				);

				// If existing invitation found, use its data
				if (existingInvitation) {
					newTickets.push({
						id: `${article.id}_${i}_existing`,
						baseItemId: article.id,
						ticketNumber: i + 1,
						isForOrderer: existingInvitation.is_for_orderer === 1,
						invitee: {
							email: existingInvitation.email || "",
							firstName: existingInvitation.first_name || "",
							lastName: existingInvitation.last_name || ""
						},
						preferences: existingInvitation.preferences || [],
						availableAgendas: selectable_agendas,
						requiredAgendaAmount: requiredAgendaAmount
					});
				} else {
					// Create new ticket (first ticket is for orderer by default)
					newTickets.push({
						id: `${article.id}_${i}_${Date.now()}`,
						baseItemId: article.id,
						ticketNumber: i + 1,
						isForOrderer: i === 0 && !newTickets.some(t => t.isForOrderer),
						invitee: {
							email: i === 0 ? formData.email : "",
							firstName: i === 0 ? formData.name : "",
							lastName: i === 0 ? formData.lastName : ""
						},
						preferences: [],
						availableAgendas: selectable_agendas,
						requiredAgendaAmount: requiredAgendaAmount
					});
				}
			}
		});

		// Debug log for created tickets
		logDebug("Generated tickets:", {
			ticketCount: newTickets.length,
			firstTicket: newTickets[0] || "No tickets generated"
		});

		return newTickets;
	}, [formData.email, formData.name, formData.lastName, logDebug]);

	/**
	 * Updates a ticket in the expandedTickets state
	 * @param {Object} updatedTicket - The updated ticket object
	 */
	const handleTicketChange = useCallback((updatedTicket) => {
		setExpandedTickets(prev =>
			prev.map(ticket => ticket.id === updatedTicket.id ? updatedTicket : ticket)
		);
	}, []);

	/**
	 * Synchronizes ticket data with article data
	 * @param {Array} articles - Array of article objects
	 * @param {Object} currentCheckoutData - Current checkout data
	 */
	const syncTicketsWithArticles = useCallback((articles, currentCheckoutData) => {
		// Check if we have any articles that are event type
		const hasEventArticles = currentCheckoutData?.item?.article?.some(article => article?.instance?.kind_id === 3);
		if (!hasEventArticles) {
			setExpandedTickets([]);
			return;
		}

		// Generate new tickets based on updated articles
		const newTickets = generateTicketsForEventArticles(articles, []);

		// Preserve existing ticket data where possible
		if (newTickets.length > 0) {
			setExpandedTickets(prev => {
				// For each new ticket, try to find matching existing ticket
				return newTickets.map(newTicket => {
					const existingTicket = prev.find(t =>
						t.baseItemId === newTicket.baseItemId &&
						t.ticketNumber === newTicket.ticketNumber
					);

					// If found, preserve its data but update agendas and requirements
					if (existingTicket) {
						return {
							...existingTicket,
							availableAgendas: newTicket.availableAgendas,
							requiredAgendaAmount: newTicket.requiredAgendaAmount
						};
					}

					// Otherwise use the new ticket
					return newTicket;
				});
			});
		}
	}, [generateTicketsForEventArticles]);

	/**
	 * Update tickets when personal information changes
	 * @param {string} fieldName - Name of the field that changed
	 * @param {string} value - New value for the field
	 */
	const updateTicketsWithPersonalInfo = useCallback((fieldName, value) => {
		if (fieldName === "email" || fieldName === "name" || fieldName === "lastName") {
			setExpandedTickets(prev => prev.map(ticket => {
				if (ticket.isForOrderer) {
					return {
						...ticket,
						invitee: {
							...ticket.invitee,
							email: fieldName === "email" ? value : ticket.invitee.email,
							firstName: fieldName === "name" ? value : ticket.invitee.firstName,
							lastName: fieldName === "lastName" ? value : ticket.invitee.lastName
						}
					};
				}
				return ticket;
			}));
		}
	}, []);

	return {
		expandedTickets,
		setExpandedTickets,
		generateTicketsForEventArticles,
		handleTicketChange,
		syncTicketsWithArticles,
		updateTicketsWithPersonalInfo
	};
};

export default useTicketManager;
